<template>
  <div class="tax-report-export">
    <button class="btn btn-outline export-icon-button" @click="openModal">
      <span class="export-icon">↓</span>
      Salary &amp; Tax Report
    </button>

    <ReuseableModal
      :modelValue="showModal"
      @update:modelValue="showModal = $event"
      title="Export Salary & Tax Report"
      submitButtonText="Export"
      :loading="exporting"
      @submit="exportReport"
    >
      <div class="tax-report-form">
        <label class="tax-report-label">Period</label>
        <select class="form-control" v-model="preset" @change="applyPreset">
          <option v-for="option in presetOptions" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </select>

        <div class="tax-report-range">
          <div class="tax-report-range-col">
            <label class="tax-report-label">From</label>
            <div class="tax-report-row">
              <select class="form-control" v-model="fromMonth" @change="preset = 'custom'">
                <option v-for="(month, index) in months" :key="month" :value="index">{{ month }}</option>
              </select>
              <select class="form-control" v-model.number="fromYear" @change="preset = 'custom'">
                <option v-for="year in years" :key="year" :value="year">{{ year }}</option>
              </select>
            </div>
          </div>
          <div class="tax-report-range-col">
            <label class="tax-report-label">To</label>
            <div class="tax-report-row">
              <select class="form-control" v-model="toMonth" @change="preset = 'custom'">
                <option v-for="(month, index) in months" :key="month" :value="index">{{ month }}</option>
              </select>
              <select class="form-control" v-model.number="toYear" @change="preset = 'custom'">
                <option v-for="year in years" :key="year" :value="year">{{ year }}</option>
              </select>
            </div>
          </div>
        </div>

        <p v-if="error" class="tax-report-error">{{ error }}</p>
        <p class="tax-report-hint">
          Downloads one workbook with two sheets: <strong>Salary</strong> and <strong>Tax Deducted</strong>,
          one row per employee (Name, NIC) and one column per month.
          Only processed payrolls are included.
        </p>
      </div>
    </ReuseableModal>
  </div>
</template>

<script>
import firebase from "firebase/app";
import "firebase/database";
import ReuseableModal from "src/components/UIComponents/ReuseableModal.vue";
import { monthNames } from "../../../assets/config/constants";
import { generateYearsArray } from "../../../assets/config/helpers";

const FISCAL_YEAR_START_MONTH = 6; // July

export default {
  name: "TaxReportExport",
  components: { ReuseableModal },
  data() {
    return {
      showModal: false,
      exporting: false,
      error: "",
      months: monthNames,
      years: generateYearsArray(),
      preset: "fiscalYear",
      presetOptions: [
        { value: "fiscalYear", label: "Current Fiscal Year (Jul - Jun)" },
        { value: "previousFiscalYear", label: "Previous Fiscal Year (Jul - Jun)" },
        { value: "calendarYear", label: "Current Calendar Year (Jan - Dec)" },
        { value: "last6Months", label: "Last 6 Months" },
        { value: "custom", label: "Custom" },
      ],
      fromMonth: 0,
      fromYear: new Date().getFullYear(),
      toMonth: 11,
      toYear: new Date().getFullYear(),
    };
  },
  methods: {
    openModal() {
      this.error = "";
      this.preset = "fiscalYear";
      this.applyPreset();
      this.showModal = true;
    },
    applyPreset() {
      const now = new Date();
      const month = now.getMonth();
      const year = now.getFullYear();
      const fiscalStartYear = month >= FISCAL_YEAR_START_MONTH ? year : year - 1;

      switch (this.preset) {
        case "fiscalYear":
          this.setRange(FISCAL_YEAR_START_MONTH, fiscalStartYear, FISCAL_YEAR_START_MONTH - 1, fiscalStartYear + 1);
          break;
        case "previousFiscalYear":
          this.setRange(FISCAL_YEAR_START_MONTH, fiscalStartYear - 1, FISCAL_YEAR_START_MONTH - 1, fiscalStartYear);
          break;
        case "calendarYear":
          this.setRange(0, year, 11, year);
          break;
        case "last6Months": {
          const start = new Date(year, month - 5, 1);
          this.setRange(start.getMonth(), start.getFullYear(), month, year);
          break;
        }
        default:
          break;
      }
    },
    setRange(fromMonth, fromYear, toMonth, toYear) {
      this.fromMonth = fromMonth;
      this.fromYear = fromYear;
      this.toMonth = toMonth;
      this.toYear = toYear;
    },
    getPeriods() {
      const periods = [];
      const end = this.toYear * 12 + this.toMonth;
      for (let index = this.fromYear * 12 + this.fromMonth; index <= end; index++) {
        const year = Math.floor(index / 12);
        const month = monthNames[index % 12];
        periods.push({ year, month, label: `${month.slice(0, 3)}-${year}` });
      }
      return periods;
    },
    getGrossSalary(payroll) {
      return Number(payroll.basicSalary || payroll.salary || 0) + Number(payroll.extraAllowance || 0);
    },
    async exportReport() {
      this.error = "";
      if (this.fromYear * 12 + this.fromMonth > this.toYear * 12 + this.toMonth) {
        this.error = "The 'From' period must be before the 'To' period.";
        return;
      }

      this.exporting = true;
      try {
        const periods = this.getPeriods();
        const snapshots = await Promise.all(
          periods.map(({ year, month }) => firebase.database().ref(`payroll/${year}/${month}`).once("value"))
        );

        const usersById = {};
        (this.$store.state.auth.allUsers || []).forEach(user => {
          usersById[user.uid] = user;
        });

        // employeeId -> { name, nic, salary: { label: value }, tax: { label: value } }
        const employees = {};
        snapshots.forEach((snapshot, index) => {
          const { label } = periods[index];
          const records = snapshot.exists() ? snapshot.val() : {};
          Object.keys(records).forEach(payrollId => {
            const payroll = records[payrollId];
            if (!payroll || payroll.isSuperAdmin || !payroll.employeeId) return;
            if (String(payroll.status || "").toLowerCase() !== "processed") return;

            const user = usersById[payroll.employeeId] || {};
            if (!employees[payroll.employeeId]) {
              employees[payroll.employeeId] = {
                name: user.username || payroll.employeeName || "",
                nic: user.nicNumber || "",
                salary: {},
                tax: {},
              };
            }
            const employee = employees[payroll.employeeId];
            employee.salary[label] = (employee.salary[label] || 0) + this.getGrossSalary(payroll);
            employee.tax[label] = (employee.tax[label] || 0) + (Number(payroll.taxDeduction) || 0);
          });
        });

        const employeeList = Object.keys(employees)
          .map(id => employees[id])
          .sort((a, b) => a.name.localeCompare(b.name));

        if (!employeeList.length) {
          this.error = "No processed payroll records found for the selected period.";
          return;
        }

        const buildRows = key => employeeList.map(employee => {
          const row = { "Employee Name": employee.name, NIC: employee.nic };
          let total = 0;
          periods.forEach(({ label }) => {
            const value = Math.round(employee[key][label] || 0);
            row[label] = value;
            total += value;
          });
          row.Total = total;
          return row;
        });

        const header = ["Employee Name", "NIC"].concat(periods.map(p => p.label), ["Total"]);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(buildRows("salary"), { header }), "Salary");
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(buildRows("tax"), { header }), "Tax Deducted");

        const first = periods[0].label;
        const last = periods[periods.length - 1].label;
        XLSX.writeFile(workbook, `Salary_Tax_Report_${first}_to_${last}.xlsx`);
        this.showModal = false;
      } catch (err) {
        console.error("Error exporting salary & tax report:", err);
        this.error = "Failed to export the report. Please try again.";
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>

<style scoped>
.tax-report-form {
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: left;
}

.tax-report-label {
  font-size: 13px;
  font-weight: 600;
  margin: 4px 0 2px;
}

.tax-report-range {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.tax-report-range-col {
  flex: 1;
  min-width: 180px;
}

.tax-report-row {
  display: flex;
  gap: 6px;
}

.tax-report-error {
  color: #d9534f;
  font-size: 13px;
  margin: 4px 0 0;
}

.tax-report-hint {
  color: #777;
  font-size: 12px;
  margin: 6px 0 0;
}
</style>



// WEBPACK FOOTER //
// src/components/PayrollManagement/PayrollSummary/TaxReportExport.vue